import type { HttpErrorResponse } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';

import type { StoreConfig } from '@ngrx/store';
import { createSelector } from '@ngrx/store';

import type { SingleEntityState } from '@core/ngrx-legacy';
import { createSingleEntityStore, memoizeSelector, ngrxEvent, pipeState } from '@core/ngrx-legacy';
import type {
  Org,
  SeatKind,
  TellentCurrentUser,
  TellentCurrentUserUpdate,
  TellentMember,
  TellentPasswordChange,
  TellentPasswordChangeResponse,
} from '@core/tellent-types';
import { TELLENT_INITIAL_CURRENT_USER, TellentOrgAbilities } from '@core/tellent-types';

import { EntityStoreKey } from '../types';

export const TellentCurrentUserConfigToken = new InjectionToken('TellentCurrentUserConfigToken', {
  providedIn: 'root',
  factory: (): StoreConfig<SingleEntityState<TellentCurrentUser>> => ({
    initialState: {
      loaded: true,
      failed: false,
      pending: false,
      error: false,
      data: inject(TELLENT_INITIAL_CURRENT_USER),
    },
  }),
});

const { actions, selectors, reducer, namespace } = createSingleEntityStore<TellentCurrentUser>()(
  EntityStoreKey.TellentCurrentUser,
  {
    customActions: adapter => ({
      updatePassword: (state, payload: TellentPasswordChange) => adapter.markAsPending(state),
      updatePasswordSuccess: (state, payload: TellentPasswordChangeResponse) =>
        adapter.markAsLoaded(state),
      updatePasswordFail: ngrxEvent<HttpErrorResponse>(),

      updateCurrentUserData: (state, payload: Partial<TellentCurrentUserUpdate>) =>
        adapter.markAsPending(state),
      updateCurrentUserDataSuccess: (state, payload: TellentCurrentUser) =>
        pipeState(
          state,
          next => adapter.update(next, payload),
          next => adapter.markAsLoaded(next),
        ),

      updateCurrentUserDataFail: ngrxEvent<HttpErrorResponse>(),

      leaveCompany: (state, payload: { orgGuid: string }) => adapter.markAsPending(state),
      leaveCompanySuccess: (state, payload: TellentCurrentUser) =>
        pipeState(
          state,
          next => adapter.update(next, payload),
          next => adapter.markAsLoaded(next),
        ),
      leaveCompanyFail: ngrxEvent<HttpErrorResponse>(),

      deleteAccount: (state, payload: { code: string }) => adapter.markAsPending(state),
      deleteAccountSuccess: (state, payload: { success: boolean }) => adapter.markAsLoaded(state),
      deleteAccountFail: ngrxEvent<HttpErrorResponse>(),

      updateFeatureDiscovery: (state, payload: TellentCurrentUser['featureDiscovery']) =>
        pipeState(state, next => adapter.update(next, { featureDiscovery: payload })),

      patchCurrentOrg: (state, payload: Partial<Org>) => {
        const user = state.data;
        if (!user) return state;
        return {
          ...state,
          data: {
            ...user,
            members: user.members.map(member =>
              member.orgGuid === user.currentOrgGuid
                ? { ...member, org: { ...member.org, ...payload } }
                : member,
            ),
          },
        };
      },
    }),
  },
);

/**
 * @deprecated use "currentOrganizationMembershipSelectorSafe" instead
 */
const currentOrganizationMembershipSelector = createSelector(selectors.data, findCurrentMembership);

const currentOrganizationMembershipSelectorSafe = createSelector(
  selectors.data,
  findCurrentMembershipSafe,
);

export function findSeatByKind(seatKind: SeatKind, membership: TellentMember) {
  return membership?.seats.find(seat => seat.app.kind === seatKind);
}

const seatByKindSelector = memoizeSelector((seatKind: SeatKind) =>
  createSelector(currentOrganizationMembershipSelector, function (membership: TellentMember) {
    return membership?.seats.find(seat => seat.app.kind === seatKind);
  }),
);

const hasFullAccess = createSelector(currentOrganizationMembershipSelector, member =>
  member?.abilities.includes(TellentOrgAbilities.ManageTellent),
);
const anyOrgFullAccess = createSelector(selectors.data, user =>
  user?.members.some(member => member.abilities.includes(TellentOrgAbilities.ManageTellent)),
);
const isAuthorizedWith = createSelector(selectors.data, (user?: TellentCurrentUser) =>
  user ? user.isAuthorizedWith : null,
);

const customTheme = createSelector(
  currentOrganizationMembershipSelectorSafe,
  member => member?.org.customTheme,
);

const TellentCurrentUserSelectors = {
  ...selectors,
  currentOrganizationMembershipSelectorSafe,
  currentOrganizationMembershipSelector,
  customTheme,
  seatByKindSelector,
  anyOrgFullAccess,
  hasFullAccess,
  isAuthorizedWith,
};

export function findCurrentMembershipSafe(user: TellentCurrentUser | null) {
  const member = user?.members?.find(member => member.orgGuid === user.currentOrgGuid) ?? null;
  return member
    ? { ...member, hasFullAccess: member.abilities.includes(TellentOrgAbilities.ManageTellent) }
    : null;
}

/**
 * @deprecated use "findCurrentMembershipSafe" instead
 */
export function findCurrentMembership(user: TellentCurrentUser): TellentMember {
  const member = user.members.find(member => member.orgGuid === user.currentOrgGuid)!;
  return { ...member, hasFullAccess: member.abilities.includes(TellentOrgAbilities.ManageTellent) };
}

export function fallbackCurrentMembership(user: TellentCurrentUser): TellentMember {
  return user.members.find(member => member.userGuid === user.guid)!;
}

export function isOrgGuidMatchingUserOrg(companyGuid: string, user: TellentCurrentUser) {
  if (!companyGuid) return false;
  return user.members.some(member => member.orgGuid === companyGuid);
}

export {
  actions as TellentCurrentUserEntityActions,
  TellentCurrentUserSelectors,
  reducer as TellentCurrentUserReducer,
  namespace as TellentCurrentUserNamespace,
};
